import { NavLink } from 'react-router-dom'
import { makeInternalURL } from './ActivityRouter'
import BloodPressureImage from './HealthRiskCalculatorBloodPressure.png'
import BodyMassIndexImage from './HealthRiskCalculatorBodyMassIndex.png'

import { agileAlliance, agileAllianceAgile101, productBacklog, sprintPlanning, sprintBacklog, scrum, userStory, userStoryTemplate } from '../DataAndAPIs/Links'

const HEALTH_RISK_CALCULATOR_TEAM_WEB_NAME = 'Health Insurance Risk Calculator for the Web As A Team'
export const HEALTH_RISK_CALCULATOR_TEAM_WEB_ROUTE = 'health-risk-calculator-team-web'
export const healthRiskCalculatorTeamWebLink = () => {return (<NavLink to={makeInternalURL(HEALTH_RISK_CALCULATOR_TEAM_WEB_ROUTE)}>{HEALTH_RISK_CALCULATOR_TEAM_WEB_NAME}</NavLink>)}
export const healthRiskCalculatorTeamWeb  = () => { return HealthRiskCalculatorTeam(HealthRiskCalculatorTeamWebWithNodeJSProperties) }

const HealthRiskCalculatorTeamWebWithNodeJSProperties = {
	'name': 'Health Insurance Risk Calculator for the Web As A Team', 
	'language': 'Web',	
	'prerequisites': 'HTML, CSS, JavaScript, GitHub, and Microsoft Azure',
	'type': 'Web application using HTML, CSS, and JavaScript',
}	
	
const HealthRiskCalculatorTeam = () => { return ( <div>
<br />
<h3>Health Insurance Risk Calculator for the Web As A Team</h3>
<p>In this assignment, you will work as a team to create an Node.js based API that will calculate the health risk for 
potential customers of Health Insurance, Inc.</p>

<p>Health Insurance, Inc. utilizes an Azure-hosted Software-as-a-Service (SaaS) architecture utilizing GitHub for 
all source code and Node.js based Application Programming Interfaces (APIs). To guarantee consistency across the 
organization, <em>all calculation and data validation must be implemented in the server-based API.</em></p>

<p>As part of this project, Health Insurance, Inc. will also require a separate web application hosted in Azure as a 
static web application. The client application needs to be user-friendly with a focus on allowing non-medical 
non-technical customers to easily enter their health information and receive a risk assessment. Clear instructions
within the application are essential as no additional training or guidance will be provided. The client application 
can also perform validation to ensure data integrity before sending the request to the Node.js API.</p>

<p>Prerequisites: GitHub, Microsoft Azure, Node.js, HTML, CSS, JavaScript</p>

<h3>Requirements:</h3>

<h5>Blood Pressure</h5>
<p>Blood pressure is measured using two numbers: systolic and diastolic. This table helps us categorize blood pressure 
using these two numbers.</p>
<div style={{display: "flex", justifyContent: "center", alignItems: "center"}}>
<img src={BloodPressureImage} alt='Eric Pogue' width= '512'/>
</div>
<br />

<p>This chart should be shown in the client application and customers should be able to select their systolic and diastolic 
blood pressure from a reasonable range of options in a drop-down list.</p>

<h5>Body-Mass Index (BMI)</h5>
<p>Body-Mass Index, which is equal to a person's weight in kg divided by the height in meters squared, is used to describe 
people as being normal weight, overweight, or obese, according to the following table.</p>
<div style={{display: "flex", justifyContent: "center", alignItems: "center"}}>
<img src={BodyMassIndexImage} alt='Eric Pogue' width= '384'/>
</div>
<br />

<p>Since this application is initially being deployed to the United States, all inputs must be collected in English units
with Height in feet and inches and Weight in pounds. Ease of data entry is critical to the success of this application, so 
spend any necessary time to design data entry forms that are fast, friendly, and intuitive. </p>

<h5>Risk Calculation</h5>
<p>In this assignment, we will determine a potential customer's coverage risk by assigning penalties for various conditions 
related to age, bmi, blood pressure, and family history. These penalties will be expressed in points. Here is how the point 
values should be calculated.</p>

<pre>{`
Age:
	< 30		- 0 points
	< 45		- 10 points
	< 60		- 20 points
	>= 60		- 30 points

BMI:
	normal		- 0 points
	overweight	- 30 points
	obese		- 75 points

Blood Pressure:
	normal		- 0 points
	elevated	- 15 points
	stage 1		- 30 points
	stage 2 	- 75 points
	crisis		- 100 points

Family Disease:
	diabetes	- 10 points
	cancer		- 10 points
	Alzheimer’s	- 10 points`}</pre>

<br />
<h5>Risk Categories</h5>
<p>Based on the total score for the potential customer, the risk category is calculated as follows:</p>
<pre>{`Total Score and Risk Category:
	<= 20		- low risk
	<= 50		- moderate risk
	<= 75		- high risk
	> 75		- uninsurable
`}</pre>

<h5>Output</h5>
<p>After the application calculates the risk score, it will display the inputs that were used in the calculation and 
the resulting risk category.</p>

<p>The application needs to allow the user to evaluate multiple potential customers until they are done.</p>

<em>Requirement 1</em>
<p>Utilize Agile software engineering {scrum()} techniques to complete {sprintPlanning()} by:</p>
<ol>
	<li>Identifying your Scrum Master and Product Owner and reviewing {agileAllianceAgile101()} from {agileAlliance()}</li>
	<li>Utilizing {sprintPlanning()}, {userStory()}, {userStoryTemplate()}, SMART, and spike/non-spike to fully groom your {productBacklog()}</li>
	<li>Utilizing your fully groomed {productBacklog()} to create your team’s {sprintBacklog()}</li>
	<li>Artifacts: fully groomed {productBacklog()}, {sprintBacklog()}, and ending {productBacklog()} named “groomed-product-backlog.pdf”, “sprint-backlog.pdf”, and “ending-product-backlog.pdf” respectively</li>
</ol>

<em>Requirement 2</em>
<p>Create a client application by:</p>
<ol>
	<li>Utilizing the SaaS architecture that includes a shared GitHub repository and an Azure static website</li>
	<li>Implementing all Health Insurance, Inc. client web application requirements list above</li>
	<li>Implementing meaningful console log messages each time an API is called or an API response is received</li>
	<li>Ensuring that NO calculations are performed in the client code</li>
</ol>

<em>Requirement 3</em>
<p>Create a Node.js and Azure-based API server by:</p>
<ol>
	<li>Utilizing the SaaS architecture that includes a shared GitHub repository and an Azure Node.js API server</li>
	<li>Implementing all Health Insurance, Inc. Node.js API server requirements list above</li>
	<li>Implements exactly 4 APIs including: ping (wake-up), bp-category, bmi, and risk-category</li>
	<li>Design the APIs so that take the minimum inputs and have succinct outputs</li>
</ol>

<em>Requirement 4</em>
<p>Implement CI/CD for all team members by:</p>
<ol>
	<li>Ensuring each team member can execute and test the client and server applications locally</li>
	<li>Ensuring all team members can clone, pull, add, commit and push changes to all shared GitHub repositories</li>
	<li>Verifying that changes made to the GitHub repositories trigger the appropriate CI/CD pipelines</li>
	<li>Verify that each team member delivers at least one Node.js API server story</li>
</ol><br />
</div> ) }

export const HealthRiskCalculatorExamples = () => {
	return (
	
<div>
<br />
<h4>Health Insurance Risk Calculator</h4>
<h5>Summary: Below are some examples of how the Health Insurance Risk Calculator user interface might look.</h5>
<br />	
<h5>Below is an example of what an implementation of the Health Insurance Risk Calculator might look like.</h5>
<pre>{`
**************************************************
HEALTH RISK CALCULATOR              
**************************************************

This program helps you classify health insurance
based on their risk level. That is determined
by their health. Their health is judged in terms
of their body-mass index, blood pressure, age,
and family history of disease.

Enter the person's age in years: 21
Enter the person's height in inches: 72
Enter the person's weight in pounds: 160
Enter the person's systolic blood pressure: 120
Enter the person's diastolic blood pressure: 70
Does the person have a family history of ...
Diabetes? n
Cancer? n
Alzheimers? n
Risk scores
Age:              0
Body-Mass:        0 (index = 21.70)
Blood Pressure:  15 (elevated)
Family History:   0

Based on these scores, the person is low-risk.

Do you have another patient? y

--------------------------------------------------

Enter the person's age in years: 31
Enter the person's height in inches: 72
Enter the person's weight in pounds: 205
Enter the person's systolic blood pressure: 115
Enter the person's diastolic blood pressure: 70
Does the person have a family history of ...
Diabetes? y
Cancer? n
Alzheimers? n
Risk scores
Age:             10
Body-Mass:       30 (index = 27.80)
Blood Pressure:   0 (normal)
Family History:  10

Based on these scores, the person is moderate-risk.

Do you have another patient? y

--------------------------------------------------

Enter the person's age in years: 47
Enter the person's height in inches: 65
Enter the person's weight in pounds: 140
Enter the person's systolic blood pressure: 125
Enter the person's diastolic blood pressure: 75
Does the person have a family history of ...
Diabetes? y
Cancer? y
Alzheimers? n
Risk scores
Age:             20
Body-Mass:        0 (index = 23.29)
Blood Pressure:  15 (elevated)

`}</pre>
</div> )

}